import React, { useState, useEffect } from "react";
import {
  Avatar,
  Avatargroup,
  Box,
  Button,
  Dropdown,
  DropdownItem,
  getColor,
  Icons,
  Tabs,
  Tooltip,
  Typography,
  useToast,
} from "fds";
import classNames from "classnames";
import "./index.scss";
import RevisionTag from "../RevisionTag";
import utilsDocs from "../../shared/utils/docs";
import utilsCommon from "../../shared/utils/common";
import AuthDotIcon from "../AuthDotIcon";
import { useNavigate } from "react-router-dom";
import useCheckboxGroup from "fds/src/hooks/useCheckboxGroup";
import useDocumentStore from "../../redux/dispatcher/useDocumentStore";
import { DocDetailDto, LogItem } from "../../types/files";
import PolicyToolTip from "../PolicyToolTip";
import { handleDocDownload } from "../../shared/utils/api";
import { useTranslation } from "react-i18next";
import { getUserInfoFromLocalStorage } from "../../shared/utils/auth";
import { LoginDto } from "../../types/auth";
import NameTag from "../NameTag";
interface DocAsideProps {
  classes?: string;
  asideInfo?: {
    syncId: string;
    revisionNo: number;
    workgroupId: string;
    contentName: string;
    documentInfo?: DocDetailDto;
  };
  page?: string;
  isPreview?: boolean;
}

const DocAside: React.FC<DocAsideProps> = ({
  asideInfo,
  isPreview,
  page,
  classes,
}) => {
  const user = getUserInfoFromLocalStorage() as string | "";
  const loginDto: LoginDto = JSON.parse(user);
  const myId = loginDto?.userInfo?.userId ?? "";

  /** Hook 정리 */
  const navigate = useNavigate();
  const toast = useToast();
  const { t } = useTranslation();
  const {
    documentState,
    getContentDetail,
    getContentFavorite,
    getContentDownload,
    getDocTimeline,
  } = useDocumentStore();
  const showLogCheckbox = useCheckboxGroup<number>();
  const { getContentThumbnail } = useDocumentStore();

  /** State 정리 */
  const [thumbnail, changeThumbnail] = useState("FAIL"); // 썸네일
  const [tab, changeTab] = useState(0); // 0 : 상세정보 , 1: 파일 이력
  const [contentDetail, changeContentDetail] = useState<
    DocDetailDto | undefined
  >(asideInfo?.documentInfo ?? undefined); // 문서 상세정보
  const isReceivedFile =
    contentDetail?.documentInfo?.workgroup?.workgroupId === "NONEWORKGROUP" &&
    myId !==
      (
        contentDetail?.documentInfo.master ??
        contentDetail?.documentInfo.masterUser
      )?.userId?.toLowerCase();
  /** useEffect 정리 */
  useEffect(() => {
    if (!asideInfo || asideInfo?.syncId === "") return;
    if (!asideInfo?.documentInfo) {
      getContentDetail({ ...asideInfo, type: "aside" }).then(() => {
        getDocTimeline({
          workgroupId: asideInfo.workgroupId,
          syncId: asideInfo.syncId,
        });
      });
    } else if (!asideInfo.documentInfo.permissionPolicyList) {
      getDocTimeline({
        workgroupId: asideInfo.workgroupId,
        syncId: asideInfo.syncId,
      });
    }
    getContentThumbnail({
      syncId: asideInfo?.syncId,
      revisionNo: asideInfo?.revisionNo,
    }).then((item) => item.payload && changeThumbnail(item.payload));
    showLogCheckbox.handleAllClick([]);
    document.getElementById("docAside")?.scrollTo(0, 0);
    changeTab(0);
  }, [asideInfo?.syncId]);
  useEffect(() => {
    if (documentState.contentAsideDetail) {
      changeContentDetail(documentState.contentAsideDetail);
    }
  }, [documentState.contentAsideDetail]);

  /** 함수 정리 */
  /** UI: 문서의 내 권한 */
  const renderDocAuth = () => {
    const myPolicy = contentDetail?.documentInfo?.myPolicy;
    const auth = !myPolicy
      ? "NONE"
      : myPolicy.uniqueId === "MASTER"
      ? "MASTER"
      : myPolicy.targetType === "USER"
      ? "IND"
      : "WORKGROUP";
    const authTitle = {
      MASTER: t("file.policy.master"),
      WORKGROUP: t("file.policy.workgroup"),
      IND: t("file.policy.ind"),
      NONE: t("file.policy.none"),
    };
    return (
      <PolicyToolTip
        policy={{
          ...myPolicy,
          targetType: myPolicy?.targetType ?? "IND",
          changeCode: "LOGIN",
          policy: myPolicy?.policy,
        }}
        targetAuth={auth}
        docInfo={contentDetail?.documentInfo}
      >
        <Box alignItems={"center"} classes={"mg mt-8"}>
          <AuthDotIcon auth={auth} style={{ marginRight: "3px" }} />
          <Typography type="body2_rg">{authTitle[auth]}</Typography>
        </Box>
      </PolicyToolTip>
    );
  };
  /** UI: 문서 상세 정보 */
  const renderDocDetail = () => {
    return (
      <Box direction="column" classes="mg mt-24 mb-32">
        <Typography type="subtitle2">{t("common.common")}</Typography>
        {!isReceivedFile && (
          <>
            <Typography
              type="body4_rg"
              exactColor={getColor("COLOR_TEXT_SECONDARY")}
              classes={"mg mt-8"}
            >
              {t("file.master")}
            </Typography>
            <NameTag
              user={{
                userName:
                  contentDetail?.documentInfo?.master?.userName?.trim() ?? "",
                userId: contentDetail?.documentInfo?.master?.userId ?? "",
                status: contentDetail?.permissionPolicyList[0]?.status ?? 1,
                userType: contentDetail?.permissionPolicyList[0]?.userType ?? 1,
              }}
              withoutUserId={false}
              height={52}
            />
            <Typography
              type="body4_rg"
              exactColor={getColor("COLOR_TEXT_SECONDARY")}
              classes={"mg mt-8"}
            >
              {t("file.updater")}
            </Typography>
            <NameTag
              user={{
                userName:
                  contentDetail?.documentInfo?.checkinUser?.userName?.trim() ??
                  "",
                userId: contentDetail?.documentInfo?.checkinUser?.userId ?? "",
                status: contentDetail?.documentInfo?.workgroup?.members
                  ? contentDetail?.documentInfo?.workgroup.members?.filter(
                      (item) =>
                        item.userId ===
                        contentDetail?.documentInfo?.checkinUser?.userId
                    )[0]?.status
                  : contentDetail?.permissionPolicyList.filter(
                      (item) =>
                        item.targetValue ===
                        contentDetail?.documentInfo?.checkinUser?.userId
                    )[0]?.status ?? 1,
                userType: contentDetail?.documentInfo?.workgroup?.members
                  ? contentDetail?.documentInfo?.workgroup.members?.filter(
                      (item) =>
                        item.userId ===
                        contentDetail?.documentInfo?.checkinUser?.userId
                    )[0]?.userType
                  : contentDetail?.permissionPolicyList.filter(
                      (item) =>
                        item.targetValue ===
                        contentDetail?.documentInfo?.checkinUser?.userId
                    )[0]?.userType ?? 1,
              }}
              withoutUserId={false}
              height={52}
            />
          </>
        )}
        <Box height={54} direction="column" justifyContent="center">
          <Typography
            type="body4_rg"
            exactColor={getColor("COLOR_TEXT_SECONDARY")}
          >
            {contentDetail?.documentInfo.workgroup.workgroupId ===
            "NONEWORKGROUP"
              ? t("sendingFile.sendDate")
              : t("file.uploadTime")}
          </Typography>
          <Typography type="body2_rg">
            {utilsCommon.renderDateFormat(
              contentDetail?.documentInfo?.createTime
            )}
          </Typography>
        </Box>
        <Box height={54} direction="column" justifyContent="center">
          <Typography
            type="body4_rg"
            exactColor={getColor("COLOR_TEXT_SECONDARY")}
          >
            {t("file.lastUpdate")}
          </Typography>
          <Typography type="body2_rg">
            {utilsCommon.renderDateFormat(
              contentDetail?.documentInfo?.updateTime
            )}
          </Typography>
        </Box>
        {contentDetail?.folderInfo &&
          !isPreview &&
          contentDetail?.documentInfo?.myPolicy?.uniqueId !== "ONLYVIEW" && (
            <Box height={66} direction="column" justifyContent="center">
              <Typography
                type="body4_rg"
                exactColor={getColor("COLOR_TEXT_SECONDARY")}
              >
                {t("file.location")}
              </Typography>
              <Box height={40} alignItems={"center"}>
                {contentDetail?.folderInfo?.length > 3 && (
                  <Box alignItems="center">
                    <Dropdown
                      btnSize={"sm"}
                      dropdownMaxWidth={184}
                      dropdownMaxHeight={360}
                      btnIcon={<Icons variant="line" label="moreVertical" />}
                    >
                      {contentDetail?.folderInfo
                        ?.filter(
                          (item, index) =>
                            index < contentDetail?.folderInfo?.length - 3
                        )
                        ?.map((item) => (
                          <DropdownItem
                            height={20}
                            label={
                              item.folderId === item.workgroupId
                                ? contentDetail?.documentInfo?.workgroup
                                    ?.workgroupName
                                : item.folderName
                            }
                            onClick={() => {
                              navigate(
                                `${process.env.PUBLIC_URL}/workgroup/${
                                  contentDetail?.documentInfo?.workgroup
                                    .workgroupId
                                }/file${
                                  item.folderName !== "HOME"
                                    ? "?folderId=" + item.folderId
                                    : ""
                                }`
                              );
                            }}
                          />
                        ))}
                    </Dropdown>
                    <Icons
                      variant="line"
                      label="chevron"
                      direction="right"
                      stroke={getColor("COLOR_ICON_INFO")}
                    />
                  </Box>
                )}
                {contentDetail?.folderInfo
                  ?.filter(
                    (item, index) =>
                      index >= contentDetail?.folderInfo?.length - 3
                  )
                  ?.map((item, index1) => {
                    return (
                      <Box alignItems="center">
                        <Tooltip
                          message={
                            item.folderId === item.workgroupId
                              ? contentDetail?.documentInfo?.workgroup
                                  ?.workgroupName
                              : item.folderName
                          }
                          place="left"
                        >
                          <Typography
                            maxWidth={index1 === 0 ? 70 : 47}
                            ellipsis
                            type="body2_rg"
                            style={{ margin: 0 }}
                            classes={"pointer cursor"}
                            onClick={() => {
                              navigate(
                                `${process.env.PUBLIC_URL}/workgroup/${
                                  contentDetail?.documentInfo?.workgroup
                                    .workgroupId
                                }/file${
                                  item.folderName !== "HOME"
                                    ? "?folderId=" + item.folderId
                                    : ""
                                }`
                              );
                            }}
                          >
                            {item.folderId === item.workgroupId
                              ? contentDetail?.documentInfo?.workgroup
                                  ?.workgroupName
                              : item.folderName}
                          </Typography>
                        </Tooltip>
                        {item.folderId !==
                          contentDetail?.folderInfo[
                            contentDetail?.folderInfo?.length - 1
                          ]?.folderId && (
                          <Icons
                            variant="line"
                            label="chevron"
                            direction="right"
                            stroke={getColor("COLOR_ICON_INFO")}
                          />
                        )}
                      </Box>
                    );
                  })}
              </Box>
            </Box>
          )}
        {contentDetail?.documentInfo?.myPolicy?.uniqueId !== "ONLYVIEW" && (
          <Box direction="column" classes="mg mt-24">
            <Typography type="subtitle2">{t("file.myPolicy")}</Typography>
            {renderDocAuth()}
          </Box>
        )}
        {contentDetail?.documentInfo?.myPolicy?.uniqueId === "ONLYVIEW" ? (
          <></>
        ) : contentDetail?.documentInfo?.workgroup.workgroupId !==
          "NONEWORKGROUP" ? (
          <Box direction="column" classes="mg mt-24">
            <Typography type="subtitle2">{t("common.workgroup")}</Typography>
            <Box direction={"column"} classes="mg mt-8">
              <Typography
                type="body4_rg"
                exactColor={getColor("COLOR_TEXT_SECONDARY")}
              >
                {t("common.name")}
              </Typography>
              <Box alignItems={"center"} classes="mg mt-8">
                <Avatar
                  uniqueCode={
                    contentDetail?.documentInfo?.workgroup.workgroupId ?? ""
                  }
                  shape={"workgroup"}
                  size={"20"}
                  name={contentDetail?.documentInfo?.workgroup.workgroupName}
                />
                <Typography
                  type="body2_rg"
                  classes={classNames("mg ml-4", {
                    "hover-text-underline": !isPreview,
                  })}
                  ellipsis
                  onClick={() =>
                    !isPreview &&
                    navigate(
                      `${process.env.PUBLIC_URL}/workgroup/${contentDetail?.documentInfo?.workgroup.workgroupId}/home`
                    )
                  }
                >
                  {contentDetail?.documentInfo?.workgroup
                    ? contentDetail?.documentInfo?.workgroup.workgroupName
                    : "My Box"}
                </Typography>
              </Box>

              <Typography
                type="body4_rg"
                classes="mg mt-10 mb-8"
                exactColor={getColor("COLOR_TEXT_SECONDARY")}
              >
                {t("common.member")}
              </Typography>
              {contentDetail?.documentInfo?.workgroup?.members &&
                contentDetail?.documentInfo?.workgroup?.members?.length > 0 && (
                  <Tooltip
                    message={contentDetail?.documentInfo?.workgroup?.members
                      ?.map((item) => item?.userName || item?.userId)
                      ?.join(", ")}
                    place="left"
                  >
                    <Avatargroup
                      size={"32"}
                      items={utilsCommon.sortMembersToAvatarGroup(
                        contentDetail?.documentInfo?.workgroup?.members,
                        contentDetail?.documentInfo?.workgroup?.managerId
                      )}
                    />
                  </Tooltip>
                )}
            </Box>
          </Box>
        ) : (
          !isReceivedFile && (
            <Box direction="column" classes="mg mt-24">
              <Typography type="subtitle2">
                {t("sendingFile.permissionUser")}
              </Typography>
              <Tooltip
                message={contentDetail?.permissionPolicyList
                  ?.map((item) => item.userName || item.targetValue)
                  ?.join(", ")}
                place="left"
              >
                <Avatargroup
                  size={"32"}
                  classes="mg mt-8"
                  items={contentDetail?.permissionPolicyList?.map((item) => {
                    return {
                      uniqueCode: utilsCommon.getUserColor(
                        item.userType,
                        item.status,
                        item?.userName
                      ),
                      name: item.userName || item.targetValue,
                      icon: item.userName ? undefined : "user",
                    };
                  })}
                />
              </Tooltip>
            </Box>
          )
        )}
      </Box>
    );
  };
  /*
  파일 이력 관련 로그
  logType 이 document:
    - actionTypeDbValue sign: 의견 등록
    - actionTypeDbValue unwrap: Wrapping 해제
    - actionTypeDbValue revision: 리비전 완료
    - actionTypeDbValue view: 열람
    - actionTypeDbValue download: 다운로드
    - actionTypeDbValue succession: 파일 관리자 승계
    - actionTypeDbValue change: 파일명 수정
    2,랑 4 해야함
  logType이 documentRole
    - targetType 이 User일 경우
      -- policyCode 이 DENY_PERMISSION 일 경우, 권한 회수로 표시
      -- add나 change는 모두 수정으로 표시
    - targetType 이 User이 아닌 경우
      -- actionTypeDbValue change: 기본 권한 수정
      -- actionTypeDbValue add:: 기본 권한 부여
      -- actionTypeDbValue delete: 기본 권한 삭제
 */
  const getLogTitle = (log: LogItem) => {
    const userName =
      !log.userName || log.userName === "#NONE_USER_NAME#"
        ? t("common.unAuthUser")
        : log.userName;
    if (log.logTypeDbValue === "document")
      switch (log.actionTypeDbValue) {
        case "succession":
          return t("file.timeline.save2", {
            name: userName,
            newMaster: log.logData,
          });
        case "change":
          return t("file.timeline.save4", {
            name: userName,
            docName: log.logData,
          });
        case "sign":
          return t("file.timeline.save5", {
            name: userName,
            memo: "값 대입 불가5",
          });
        case "unwrap":
          return t("file.timeline.save3", {
            name: userName,
          });
        case "revision":
          return t("file.timeline.save7", {
            name: userName,
          });
        case "view":
          return t("file.timeline.save6", {
            name: userName,
          });
        case "download":
          return t("file.timeline.save8", {
            name: userName,
          });
        case "add":
          return t("file.timeline.save9", {
            name: userName,
          });
      }
    else if (
      log.policyLogs &&
      log.logTypeDbValue === "documentRole" &&
      ["change", "add", "delete"].includes(log.actionTypeDbValue)
    ) {
      const targetPolicy = log.policyLogs[0].permissionPolicy;
      if (targetPolicy.targetType === "USER") {
        // if (
        //   log.policyLogs.length === 1 &&
        //   targetPolicy.authType === "DENY" &&
        //   targetPolicy.changeCode !== "DELETE"
        // ) {
        //   return t("file.timeline.save1.deleteTo", {
        //     name: userName,
        //     reciever: targetPolicy.targetValue,
        //   });
        // } else if (targetPolicy.changeCode === "DELETE") {
        //   return t("file.timeline.save1.deleteDefault", {
        //     name: userName,
        //   });
        // } else
        return t("file.timeline.save1.changeTo", {
          name: userName,
          reciever: log.policyLogs
            .map((item) => item.permissionPolicy.targetValue)
            .join(", "),
        });
      } else {
        switch (log.actionTypeDbValue) {
          case "change":
            return t("file.timeline.save1.changeDefault", {
              name: userName,
            });
          case "add":
            return t("file.timeline.save1.createDefault", {
              name: userName,
            });
          case "delete":
            return t("file.timeline.save1.deleteDefault", {
              name: userName,
            });
        }
      }
    }
  };
  /** UI: 파일 이력 */
  const renderDocHistory = () => {
    return (
      <Box direction="column" classes="mg mt-8 docAside-history">
        {contentDetail?.timeline?.timelineList?.map((item) => (
          <>
            <Box classes="docAside-historyItem revision">
              <RevisionTag revisionNo={item.revisionMap.no} size={"md"} />
              <Box
                classes={"mg ml-8"}
                direction="column"
                width={"calc(100% - 40px)"}
              >
                <Box alignItems={"center"}>
                  <Typography
                    type="body2_md"
                    ellipsis
                    whiteSpace="pre"
                    fontWeight={700}
                  >
                    {item.revisionMap.checkinUser.userName}
                  </Typography>
                  <Typography
                    classes="mg ml-4"
                    type="body2_rg"
                    whiteSpace="pre"
                  >
                    {t("file.versionUpdate")}
                  </Typography>
                </Box>
                <Box classes="mg mt-2">
                  <Typography
                    type="body4_rg"
                    exactColor={getColor("COLOR_TEXT_SECONDARY")}
                  >
                    {utilsDocs.fileSizeFormat(item.revisionMap.fileSize)}
                  </Typography>
                  <Typography
                    type="body4_rg"
                    classes="mg ml-4"
                    exactColor={getColor("COLOR_TEXT_SECONDARY")}
                  >
                    {" • " +
                      utilsCommon.renderDateFormat(item.revisionMap.regDate)}
                  </Typography>
                </Box>

                <Box classes="docAside-actions">
                  {contentDetail?.documentInfo?.myPolicy?.uniqueId !==
                    "ONLYVIEW" && (
                    <Tooltip message={t("file.action.download")}>
                      <Button
                        btnStyle="icon"
                        size={"sm"}
                        onClick={() =>
                          handleDocDownload(
                            contentDetail.documentInfo.syncId,
                            getContentDownload,
                            toast,
                            t,
                            item.revisionMap.no + ""
                          )
                        }
                        leadingIcon={<Icons variant="line" label="download" />}
                      />
                    </Tooltip>
                  )}
                  <Button
                    btnStyle="icon"
                    size={"sm"}
                    onClick={() =>
                      showLogCheckbox.handleClick(item.revisionMap.no)
                    }
                    leadingIcon={
                      <Icons
                        variant="line"
                        label="chevron"
                        direction={
                          showLogCheckbox.isSelected(item.revisionMap.no)
                            ? "up"
                            : "down"
                        }
                      />
                    }
                    classes="mg ml-4"
                  />
                </Box>
              </Box>
            </Box>
            {showLogCheckbox.isSelected(item.revisionMap.no) &&
              item.log.map((item1: LogItem, index1) => (
                <Box classes="docAside-historyItem log">
                  {index1 === 0 ? (
                    <Icons
                      variant="line"
                      label="downRight"
                      stroke={getColor("COLOR_ICON_INTERACTIVE_SECONDARY")}
                    />
                  ) : (
                    <Box width={24} classes={"mg mt-4"} />
                  )}
                  <Avatar
                    name={
                      !item1.userName || item1.userName === "#NONE_USER_NAME#"
                        ? t("common.unAuthUser")
                        : item1.userName
                    }
                    uniqueCode={utilsCommon.getUserColor(
                      item1?.userType,
                      item1?.status ?? undefined,
                      item1?.userName
                    )}
                    icon={item1.userName ? undefined : "user"}
                    size="32"
                    classes="mg ml-4"
                  />
                  <Box
                    classes={"mg ml-8"}
                    direction="column"
                    width={"calc(100% - 40px)"}
                  >
                    <Box alignItems={"center"}>
                      <Typography type="body2_rg">
                        {item1.policyLogs &&
                        item1.policyLogs.length === 1 &&
                        (item1.workgroupId !== "NONEWORKGROUP" ||
                          "delete" !== item1.actionTypeDbValue) ? (
                          <PolicyToolTip
                            policy={item1.policyLogs[0].permissionPolicy}
                            targetAuth={
                              ["delete"].includes(item1.actionTypeDbValue) ||
                              (item1.actionTypeDbValue === "add" &&
                                item1.policyLogs[0].permissionPolicy
                                  .targetType === "WORKGROUP")
                                ? "WORKGROUP"
                                : item1.policyLogs[0].permissionPolicy
                                    .policyCode === "DENY_PERMISSION"
                                ? "NONE"
                                : "IND"
                            }
                            docInfo={contentDetail?.documentInfo}
                            showTooltip
                          >
                            {getLogTitle(item1)}
                          </PolicyToolTip>
                        ) : (
                          getLogTitle(item1)
                        )}
                      </Typography>
                    </Box>
                    <Typography
                      type="body4_rg"
                      classes="mg mt-2"
                      exactColor={getColor("COLOR_TEXT_SECONDARY")}
                    >
                      {utilsCommon.renderDateFormat(item1.logDate)}
                    </Typography>
                  </Box>
                </Box>
              ))}
          </>
        ))}
      </Box>
    );
  };
  return !asideInfo?.syncId || !contentDetail ? (
    <Box
      classes={classNames("docAside", classes)}
      id="docAside"
      alignItems="center"
    >
      <Box height={160} alignItems="center" justifyContent="center">
        <img
          src={require("../../shared/images/pages/File/EmptyDetail.png")}
          alt={"thumbnail"}
          width={140}
          height={110}
        />
      </Box>
      <Typography type="body2_rg" exactColor={getColor("COLOR_TEXT_SECONDARY")}>
        {t("file.chooseDoc")}
      </Typography>
    </Box>
  ) : (
    <Box
      classes={classNames("docAside", classes, {
        isPreview: isPreview,
        log: page === "log",
      })}
      id="docAside"
    >
      {isPreview ? (
        <Box>
          {utilsDocs.renderDocIcon(
            contentDetail?.documentInfo?.contentName,
            24
          )}
          <Box direction="column" classes="mg ml-4" width="248px">
            <Tooltip
              message={contentDetail?.documentInfo?.contentName}
              place="left"
            >
              <Typography type="body2_rg" classes="docAside-title">
                {contentDetail?.documentInfo?.contentName}
              </Typography>
            </Tooltip>

            <Typography
              type="body4_rg"
              exactColor={getColor("COLOR_TEXT_SECONDARY")}
            >
              {`v${
                contentDetail?.documentInfo?.revisionNo
              } • ${utilsDocs.fileSizeFormat(
                contentDetail?.documentInfo?.fileSize
              )} • ${utilsDocs.fileFormattoStr(
                contentDetail?.documentInfo?.contentName
              )}`}
            </Typography>
          </Box>

          {page !== "clientDocDetail" &&
            contentDetail?.documentInfo?.myPolicy?.uniqueId !== "ONLYVIEW" && (
              <Tooltip
                message={
                  contentDetail.documentInfo.favoritDate === 0
                    ? t("file.action.addFav")
                    : t("file.action.removeFav")
                }
                place="left"
              >
                <Button
                  btnStyle="icon"
                  classes="mg ml-auto"
                  onClick={() =>
                    getContentFavorite({
                      flag: contentDetail?.documentInfo?.favoritDate === 0,
                      syncIds: [asideInfo?.syncId],
                    })
                  }
                  leadingIcon={
                    contentDetail?.documentInfo?.favoritDate > 0 ? (
                      <Icons variant="fill" label="star" isColored size={20} />
                    ) : (
                      <Icons variant="line" label="star" isOn size={20} />
                    )
                  }
                />
              </Tooltip>
            )}
        </Box>
      ) : (
        <Box classes="docAside-graybox">
          <Box classes="docAside-thumbnail">
            {contentDetail?.documentInfo?.status === "T" ? (
              <Box classes="mg mb-12">
                {utilsDocs.renderDocIcon(
                  contentDetail?.documentInfo?.contentName,
                  60
                )}
              </Box>
            ) : thumbnail === "FAIL" ? (
              utilsDocs.renderDocIcon(asideInfo?.contentName, 128)
            ) : (
              <img
                style={{ objectFit: "fill" }}
                src={`data:image/png;base64,${thumbnail}`}
                alt={"thumbnail"}
                height={"100%"}
              />
            )}
            {contentDetail?.documentInfo?.favoritDate > 0 && (
              <Icons variant="fill" label="star" isColored classes="star" />
            )}
          </Box>
          <Box classes="mg mt-4 docAside-titleBar">
            <Tooltip
              message={contentDetail?.documentInfo?.contentName}
              place="left"
            >
              <Typography type="subtitle2" classes="docAside-title">
                {contentDetail?.documentInfo?.contentName}
              </Typography>
            </Tooltip>
          </Box>
          <Box classes="mg mt-4">
            {contentDetail?.documentInfo?.status !== "T" && (
              <Typography
                type="body4_rg"
                exactColor={getColor("COLOR_TEXT_SECONDARY")}
              >{`v${contentDetail?.documentInfo?.revisionNo} • `}</Typography>
            )}
            <Typography
              type="body4_rg"
              exactColor={getColor("COLOR_TEXT_SECONDARY")}
            >
              {`${utilsDocs.fileSizeFormat(
                contentDetail?.documentInfo?.fileSize
              )}`}
            </Typography>
            <Typography
              type="body4_rg"
              classes="mg ml-4"
              exactColor={getColor("COLOR_TEXT_SECONDARY")}
            >
              {` • ${utilsDocs.fileFormattoStr(
                contentDetail?.documentInfo?.contentName
              )}`}
            </Typography>
          </Box>
        </Box>
      )}
      {contentDetail?.documentInfo?.status === "T" ? (
        <Box classes="mg mt-24" direction="column">
          <Typography type="subtitle2">{t("file.uploadTime")}</Typography>
          <Typography type="body2_rg" classes="mg mt-8">
            {utilsCommon.renderDateFormat(
              contentDetail?.documentInfo?.createTime
            )}
          </Typography>
        </Box>
      ) : (
        <Box direction={"column"}>
          {!isReceivedFile && (
            <Tabs
              tabs={[t("file.detail"), t("file.version")].map((item) => {
                return { text: item, color: "default", isNew: false };
              })}
              size="md"
              type="full"
              selected={tab}
              selectTab={(tab) => changeTab(tab)}
              classes="mg mt-24"
            />
          )}
          {tab === 0 ? renderDocDetail() : renderDocHistory()}
        </Box>
      )}
    </Box>
  );
};
export default DocAside;
